// src/writing-engine.js

const NARRATIVE_ENGINE = `
## Narrative

Treat every section as a story rather than a sequence of facts.

Readers should repeatedly experience:
Curiosity → Discovery → Revised understanding.

Prefer causal progression over chronological progression.

Each section should naturally:
• begin with an observation, mystery, paradox or surprising consequence;
• raise curiosity before providing explanations;
• reveal ideas progressively instead of immediately;
• end by expanding the reader's understanding.

Every 2–3 paragraphs introduce at least one of:
- surprise
- contradiction
- failed assumption
- unexpected implication
- historical conflict
- new perspective

Facts should feel discovered, not delivered.

Write as though every paragraph earns the next one.
`;

const RHYTHM_ENGINE = `
## Rhythm

Avoid mechanical rhythm.

Intentionally vary:

• sentence length
• paragraph length
• pacing
• emphasis

Alternate naturally between:

- short impactful observations;
- medium explanatory passages;
- occasional longer flowing paragraphs that build tension or anticipation.

Allow longer passages to build momentum.

Release them with short observations.

Occasionally let a single sentence stand alone for emphasis.

Avoid:

- repetitive paragraph sizes;
- repetitive sentence structures;
- repetitive openings;
- repetitive transitions.

The prose should feel spoken rather than generated.
`;

const COGNITIVE_ENGINE = `
## Reader Experience

The objective is not only to explain.

Continuously guide the reader through:

"I didn't know that."

↓

"Wait..."

↓

"Really?"

↓

"Oh..."

↓

"Now I understand."

↓

"That's fascinating."

Curiosity should increase before it is satisfied.

Delay certainty slightly.

If more than two explanatory paragraphs pass without changing the reader's understanding, internally restructure the explanation.

Alternate new information with brief moments of reflection or implication.

Wonder should emerge naturally from reality itself.
`;

const WONDER_ENGINE = `
## Wonder

Reality is already astonishing.

Never exaggerate.

Occasionally pause to let remarkable facts breathe before moving on.

Wonder should arise from the implications of the facts rather than emotional adjectives.
`;

const NATURAL_VOICE_ENGINE = `
## Natural Voice

Write like an experienced human author.

Avoid:

- repetitive rhetorical patterns;
- repetitive paragraph openings;
- identical sentence structures;
- formulaic storytelling;
- predictable transitions such as "however", "meanwhile", "furthermore", "in conclusion" repeated mechanically.

Prefer natural variation over perfect consistency.

Slight imperfection is preferable to mechanical regularity.
`;

const READER_MODEL = `
## Reader Model

Continuously imagine an intelligent and curious reader.

Predict where they may become:

- bored;
- confused;
- overloaded;
- unsurprised.

Adjust pacing, explanation and emphasis before that happens.

The reader should always feel there is a reason to continue.
`;

const QUALITY_REVIEW = `
## Internal Review

Before finishing, silently check:

• Does this resemble Wikipedia or a textbook?
→ Rewrite.

• Are there more than two explanatory paragraphs without curiosity or surprise?
→ Rewrite.

• Is the rhythm repetitive?
→ Rewrite.

• Does every section create curiosity before explanation?
→ Rewrite.

• Does the ending expand understanding instead of merely summarize?
→ Rewrite.

Never mention this review.
`;

const HUMOR_ENGINE = `
## Humor (Monty mode)

Humor comes from structure, not random jokes.

Preferred mechanisms:

- logical escalation;
- deadpan delivery;
- bureaucratic seriousness;
- misplaced confidence;
- literal interpretation;
- delayed payoff;
- impeccable logic applied to absurd situations.

Characters sincerely accept ridiculous premises.

Avoid:

- random funny comparisons;
- meme humor;
- forced punchlines;
- jokes that interrupt explanation.

Facts remain unchanged.

Comedy changes framing, never truth.
`;

const SIMPLICITY_ENGINE = `
## Simplicity (ELI8)

Reduce cognitive load, not merely vocabulary.

Never introduce two unfamiliar concepts in the same sentence.

For every new concept:

1. familiar everyday experience;
2. simple comparison;
3. intuitive explanation;
4. optional technical name.

Only then continue.

Avoid analogies requiring adult knowledge.

Prefer examples involving:

- toys;
- pets;
- bicycles;
- playgrounds;
- food;
- water;
- rain;
- balloons;
- building blocks.

Assume zero prior scientific vocabulary.
`;

export function buildWritingEngine(toneId) {
  const always = [
    NARRATIVE_ENGINE,
    RHYTHM_ENGINE,
    COGNITIVE_ENGINE,
    WONDER_ENGINE,
    NATURAL_VOICE_ENGINE,
    READER_MODEL,
    QUALITY_REVIEW,
  ].join("\n");

  const toneSpecific =
    toneId === "monty"
      ? HUMOR_ENGINE
      : toneId === "eli8"
      ? SIMPLICITY_ENGINE
      : "";

  return [always, toneSpecific].filter(Boolean).join("\n");
}

// ── C1: Opening Lottery ──────────────────────────────────────────────────────
// Same catalogue as api/writing-modules.js (server side). Necessarily a
// separate copy — this file bundles into the browser (ESM), the server one is
// CommonJS for Vercel functions. Keep wording in sync if it changes.
// No seeded assignment here: prompts.js generates single standalone stories
// (custom-topic search), not a 5-chapter track, so there's no "don't repeat
// within a track" constraint to satisfy — just pick at random each call.
export const OPENING_FORMS = [
  { id: 'scene', instruction: `Open with a concrete SCENE: a specific person, in a specific place, at a specific moment, doing something. Sensory detail in the first two sentences. No abstractions until paragraph 2.` },
  { id: 'question', instruction: `Open with ONE sharp question the reader has never thought to ask — then immediately complicate it. Do not answer it in the first paragraph.` },
  { id: 'number', instruction: `Open with a startling NUMBER or measurement, stated plainly, then make the reader feel its scale through one vivid comparison.` },
  { id: 'misconception', instruction: `Open by stating a common belief the reader probably holds — then break it in the second or third sentence. Be respectful, not smug.` },
  { id: 'in_medias_res', instruction: `Open in the MIDDLE of the story's most dramatic moment, with no setup. Context arrives only in paragraph 2.` },
  { id: 'object', instruction: `Open with a single physical OBJECT — describe it precisely, then reveal why this ordinary thing changes everything.` },
  { id: 'stakes', instruction: `Open by stating what was at STAKE — what would be lost or won — before explaining anything else.` },
  { id: 'quiet_detail', instruction: `Open with a small, quiet, almost boring detail — then zoom out to reveal it as the key to the whole story.` },
];

export function pickRandomOpening() {
  return OPENING_FORMS[Math.floor(Math.random() * OPENING_FORMS.length)];
}

// ── C2: Banned phrases ───────────────────────────────────────────────────────
export const BANNED_PATTERNS = `## BANNED PHRASES AND PATTERNS
Never use these openings or constructions (they are AI clichés):
- "Imagine..." / "Представьте себе..." / "Picture this..."
- "But here's the thing" / "But here's where it gets interesting" / "Но вот что удивительно" / "И вот тут начинается самое интересное"
- "Little did they know" / "Он ещё не знал, что..."
- "In a world where..." / "В мире, где..."
- "takes on a whole new meaning"
- "nothing short of remarkable / extraordinary"
- "delve", "tapestry", "testament to", "underscores", "It's worth noting"
- Rhetorical triads as a crutch: "X. Y. Z." three fragments in a row — allowed max ONCE.
- Ending a paragraph with a one-word dramatic sentence ("Alone.", "Silence.") — max ONCE.
- Starting more than one paragraph with the same word.
INSTEAD: state facts plainly; earn drama through specifics (names, dates, quantities, places), not through announcing that something is dramatic.`;

// ── C4: sentence-length mechanics ────────────────────────────────────────────
export const SENTENCE_RHYTHM = `## Sentence rhythm
Vary sentence length deliberately: after any sentence longer than 25 words, the next must be under 12. At least one paragraph should read fast (short sentences), at least one should read slow (one long, winding sentence that earns its length). Paragraphs: 2-5 sentences, never more than 5.`;

// ── C7: Scene beats — screen-level micro-hooks ───────────────────────────────
// Same idea as C3's chapter-to-chapter cliffhanger, one level down: inside a
// single story, at every page-turn (client splits on [[BREAK]] — see
// paginateStory in App.jsx), so scrolling feels like cutting between short
// documentary scenes rather than reading one continuous article. Falls back
// cleanly to the old mechanical pagination if the model omits the marker.
export const SCENE_BEATS = `## Scene beats (screen-level hooks)
Structure the story as a sequence of short scenes, not one continuous flow. Each scene is roughly 2-4 paragraphs — about what fits on one phone screen.
End every scene except the very last one on a small unresolved beat: a question forming in the reader's mind, a detail that doesn't fit yet, a turn about to happen. Never resolve it in the same scene — let the next scene open into it.
Do not use the banned phrases above to manufacture this tension — earn it through what you choose to reveal and withhold, not by announcing suspense.
Insert the marker [[BREAK]] alone on its own line between each scene (never after the last one, never before the first). Do not mention this marker or the word "scene" to the reader — it is invisible plumbing, not content.`;